import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { doc, DocumentSnapshot, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';

import { db } from '../../../config';
import { AiAgentConfig } from './types';

export const AGENT_CONFIG_DOC_ID = 'config';

/** Dokument Firestore → konfiguracja agenta z wartościami domyślnymi; `null`, gdy dokumentu nie ma. */
const parseConfig = (snapshot: DocumentSnapshot): AiAgentConfig | null => {
  if (!snapshot.exists()) return null;
  const data = snapshot.data() as Partial<AiAgentConfig>;
  return {
    name: data.name ?? '',
    gender: (data.gender as AiAgentConfig['gender']) ?? 'man',
    avatarId: data.avatarId ?? 'man-1',
    customAvatarUrl: typeof data.customAvatarUrl === 'string' ? data.customAvatarUrl : undefined,
    style: (data.style as AiAgentConfig['style']) ?? 'concise',
    tone: (data.tone as AiAgentConfig['tone']) ?? 'friendly',
    useEmoji: data.useEmoji ?? false,
    rules: Array.isArray(data.rules) ? data.rules : [],
    customPersonaPrompt: typeof data.customPersonaPrompt === 'string'
      ? data.customPersonaPrompt
      : undefined,
    excludedModules: Array.isArray(data.excludedModules)
      ? data.excludedModules.filter((m): m is string => typeof m === 'string')
      : [],
    excludedOutletIds: Array.isArray(data.excludedOutletIds)
      ? data.excludedOutletIds.filter((id): id is string => typeof id === 'string')
      : [],
    isActive: data.isActive ?? true,
    createdAt: data.createdAt as number | undefined,
    updatedAt: data.updatedAt as number | undefined,
  };
};

/**
 * Realtime'owy odczyt dokumentu `hotels/{organizationId}/aiagents/config`.
 *
 * Zwraca `null` gdy konfiguracja jeszcze nie istnieje — UI powinien wtedy
 * pokazać formularz tworzenia. Gdy istnieje — formularz edycji.
 */
export const useGetAiAgentConfig = (
  organizationId: string | null | undefined,
  options?: UseQueryOptions<AiAgentConfig | null, FirebaseError>,
): UseQueryResult<AiAgentConfig | null, FirebaseError> => {
  const queryClient = useQueryClient();
  const queryKey = ['aiagents-config', organizationId] as const;

  useEffect(() => {
    if (!organizationId) return undefined;

    const ref = doc(db, 'hotels', organizationId, 'aiagents', AGENT_CONFIG_DOC_ID);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        queryClient.setQueryData(queryKey, parseConfig(snapshot));
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[useGetAiAgentConfig] error', error);
        queryClient.setQueryData(queryKey, null);
      },
    );

    return () => unsubscribe();
  }, [organizationId, queryClient]);

  return useQuery<AiAgentConfig | null, FirebaseError>({
    queryKey,
    /* Czekamy na pierwszy snapshot zamiast od razu zwracać to, co jest w cache. Wcześniej
       queryFn oddawał `null` przed odpowiedzią Firestore, a jego wynik nadpisywał konfigurację
       ustawioną już przez nasłuch: istniejący agent wyglądał jak brak agenta i panel
       pokazywał kreator tworzenia. Firestore współdzieli nasłuch, więc nie ma drugiego odczytu. */
    queryFn: () => new Promise<AiAgentConfig | null>((resolve) => {
      if (!organizationId) {
        resolve(null);
        return;
      }
      const ref = doc(db, 'hotels', organizationId, 'aiagents', AGENT_CONFIG_DOC_ID);
      const unsubscribe = onSnapshot(
        ref,
        (snapshot) => {
          unsubscribe();
          resolve(parseConfig(snapshot));
        },
        () => {
          unsubscribe();
          resolve(queryClient.getQueryData<AiAgentConfig | null>(queryKey) ?? null);
        },
      );
    }),
    enabled: !!organizationId,
    staleTime: Infinity,
    retry: 0,
    ...options,
  });
};
